import styled from 'styled-components';
import React, { FC, FormEventHandler, useEffect, useState } from 'react';
import theme from '../utils/theme';
import PrimaryButton, { SecondaryButton } from './Button';

type Exclusion = {
  apiKey: string;
  date: string;
  reason: string;
};

export const AdSpendExclusionsForm: FC<{ authToken: string }> = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [entries, setEntries] = useState<Exclusion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const authHeaders = { Authorization: 'Bearer ' + props.authToken };

  const rowId = (e: Exclusion) => `${e.apiKey}__${e.date}`;

  const fetchEntries = () => {
    fetch(`${process.env.PRIVATE_API_URL}/ad-spend-exclusions`, {
      headers: authHeaders,
      mode: 'cors',
    }).then((response) => {
      if (response.status !== 200) {
        response.text().then((body) => setError(body.trim().length > 0 ? body : 'Failed to fetch entries'));
      } else {
        response.json().then((data: Exclusion[]) => setEntries(data));
      }
    });
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleFormSubmission: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    const payload = new URLSearchParams(new FormData(form) as any);

    setIsLoading(true);
    setError(null);
    setResult(null);

    fetch(form.action, {
      method: 'post',
      body: payload,
      headers: authHeaders,
    })
      .then((response) => {
        setIsLoading(false);
        if (response.status !== 200) {
          response.text().then((body) => setError(body.trim().length > 0 ? body : 'Failed to create exclusion'));
        } else {
          setResult('Exclusion added successfully');
          response.json().then((data: Exclusion[]) => setEntries(data));
          form.reset();
        }
      })
      .catch((e) => {
        setIsLoading(false);
        setError(e instanceof Error ? e.message : e.toString());
      });
  };

  const handleDelete = (entry: Exclusion) => {
    if (!confirm(`Remove exclusion for "${entry.apiKey}" on ${entry.date}?`)) return;

    const id = rowId(entry);
    setDeletingKey(id);
    setError(null);
    setResult(null);

    fetch(
      `${process.env.PRIVATE_API_URL}/ad-spend-exclusions/${encodeURIComponent(entry.apiKey)}/${encodeURIComponent(entry.date)}`,
      {
        method: 'delete',
        headers: authHeaders,
        mode: 'cors',
      },
    )
      .then((response) => {
        setDeletingKey(null);
        if (response.status !== 200) {
          response.text().then((body) => setError(body.trim().length > 0 ? body : 'Failed to delete exclusion'));
        } else {
          setResult('Exclusion removed successfully');
          response.json().then((data: Exclusion[]) => setEntries(data));
        }
      })
      .catch((e) => {
        setDeletingKey(null);
        setError(e instanceof Error ? e.message : e.toString());
      });
  };

  return (
    <>
      <h3>Ad Spend Exclusions</h3>

      <form method="POST" action={`${process.env.PRIVATE_API_URL}/ad-spend-exclusions`} onSubmit={handleFormSubmission}>
        <p>
          <Label htmlFor="api-key">API Key:</Label>
          <Input type="text" name="api-key" required={true} placeholder="e.g. f1f1d25e601e4cafb9c8ec0b5cc743db" />
        </p>
        <p>
          <Label htmlFor="date">Date:</Label>
          <Input type="date" name="date" required={true} />
        </p>
        <p>
          <Label htmlFor="reason">Reason:</Label>
          <Input type="text" name="reason" required={true} placeholder="e.g. onboarded too soon" />
        </p>
        <PrimaryButton disabled={isLoading} isLoading={isLoading} type="submit">
          Add Exclusion
        </PrimaryButton>

        {result !== null && <p style={{ color: theme.colours.confirmation400 }}>{result}</p>}
        {error !== null && <p style={{ color: theme.colours.alert400 }}>{error}</p>}
      </form>

      {entries.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>API Key</Th>
              <Th>Date</Th>
              <Th>Reason</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const id = rowId(entry);
              return (
                <tr key={id}>
                  <Td>{entry.apiKey}</Td>
                  <Td>{entry.date}</Td>
                  <Td>{entry.reason}</Td>
                  <Td>
                    <SecondaryButton
                      disabled={deletingKey === id}
                      isLoading={deletingKey === id}
                      onClick={() => handleDelete(entry)}
                    >
                      Remove
                    </SecondaryButton>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
};

const Label = styled.label`
  font-size: 14px;
  display: block;
  margin-bottom: 5px;
  font-weight: ${theme.fontWeightMedium};
  min-width: 200px;
`;

const Input = styled.input`
  height: 44px;
  color: ${theme.colours.black};
  top: 25px;
  left: 12px;
  line-height: 20px;
  padding-left: 12px;
  width: 350px;
`;

const Table = styled.table`
  margin-top: 20px;
  border-collapse: collapse;
  width: 100%;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${theme.colours.gray300};
  font-size: 14px;
  font-weight: ${theme.fontWeightMedium};
`;

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid ${theme.colours.gray300};
  font-size: 14px;
`;
